import { API_URLS } from '@helpers/api';
import { getSystemIcon, SystemIcon } from '@helpers/icons';
import { withMotionMax } from '@hoc/withMotionMax';
import { useForm } from '@hooks/useForm';
import Button, { LinkButton } from '@stories/Components/Buttons/Button/Button';
import CalculatorResponse from '@stories/Components/CalculatorResponse/CalculatorResponse';
import ProductCard from '@stories/Components/Cards/ProductCard/ProductCard';
import FiltersFormContainer from '@stories/Components/Misc/FiltersFormContainer/FiltersFormContainer';
import ErrorSummary from '@stories/Components/Misc/Forms/ErrorSummary';
import FormField from '@stories/Components/Misc/Forms/FormField';
import FormSection from '@stories/Components/Misc/Forms/FormSection';
import NumberField from '@stories/Components/Misc/Forms/Inputs/Number/Number';
import Select from '@stories/Components/Misc/Forms/Inputs/Select/Select';
import Textbox from '@stories/Components/Misc/Forms/Inputs/Textbox/Textbox';
import LoadingIndicator from '@stories/Components/Misc/LoadingIndicator/LoadingIndicator';
import { ContentType } from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import * as React from 'react';
import S from './AffordabilityCalculator.styles';
import { ExpenditureWeighting, IncomeWeighting, StepFields, } from './types';
const AffordabilityCalculator = (props) => {
    const [step, setStep] = React.useState(0);
    const [incomeActive, setIncomeActive] = React.useState(false);
    const [creditCardPayments, setCreditCardPayments] = React.useState('');
    const { refContainer, refCustomData, status, serverErrors, customResponseData, onSubmit, useReactHookFormReturn: { register, formState: { errors }, setValue, watch, trigger, }, } = useForm({
        defaultValues: {
            dataNode: props.dataNodeId,
            noApplicants: 1,
            noAdults: 1,
            noYoungChildren: 0,
            noChildren: 0,
            mortgageTerm: 25,
        },
        endpoint: API_URLS.affordabilityCalculator,
        mode: 'json',
        postSubmitCallback: async () => {
            setStep(5);
        },
    });
    const formData = watch();
    // update creditCardPayments to be 3% of creditCards value
    React.useEffect(() => {
        if (formData.creditCards) {
            setCreditCardPayments((formData.creditCards * 0.03).toLocaleString('en-GB', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }));
        }
        else {
            setCreditCardPayments('');
        }
    }, [formData.creditCards]);
    React.useEffect(() => {
        refContainer.current?.scrollIntoView({ behavior: 'smooth' });
        // Focus on the first input field when the step changes
        setTimeout(() => {
            refContainer.current?.querySelector('select, input')?.focus();
        }, 150);
    }, [step]);
    return (React.createElement(S.Container, { ref: refCustomData, translations: props.translations },
        React.createElement(S.TabsWrapper, { "data-complete": step === 5 }, StepFields.map((_, i) => (React.createElement(S.StepTab, { key: `affordability-step-tab-${i}`, "data-stage": step === i ? 'active' : step < i ? 'disabled' : 'complete', onClick: () => setStep(i), disabled: i > step },
            React.createElement(S.StepTabNumber, null, i + 1),
            React.createElement("span", null, props.translations?.[`forms.affordabilityCalculator.step${i + 1}.label`]))))),
        React.createElement(FiltersFormContainer, { subtitle: step === 5 && customResponseData
                ? props.translations?.['forms.affordabilityCalculator.complete.subtitle']
                : `${props.translations?.['forms.affordabilityCalculator.step.label']} ${step + 1}`, title: step === 5 && customResponseData
                ? props.translations?.['forms.affordabilityCalculator.complete.label'] ?? ''
                : `${props.translations?.[`forms.affordabilityCalculator.step${step + 1}.label`]}`, introduction: step === 5 && customResponseData
                ? props.translations?.['forms.affordabilityCalculator.complete.introduction']
                : props.translations?.[`forms.affordabilityCalculator.step${step + 1}.introduction`], type: props.type },
            React.createElement(S.FormWrapper, { ref: refContainer, onSubmit: onSubmit },
                status === 'error' && serverErrors.length > 0 && React.createElement(ErrorSummary, { errors: serverErrors }),
                step === 0 && renderStepOneFields(),
                step === 1 && renderStepTwoFields(),
                step === 2 && renderStepThreeFields(),
                step === 3 && renderStepFourFields(),
                step === 4 && renderStepFiveFields(),
                step === 5 && customResponseData && (React.createElement("div", null,
                    customResponseData.minimumAmount && (React.createElement(S.CompleteText, null, customResponseData.minimumAmount)),
                    customResponseData.borrowPrefix && (React.createElement(S.CompleteText, null, customResponseData.borrowPrefix)),
                    customResponseData.borrowAmount && (React.createElement(S.BorrowAmount, null, customResponseData.borrowAmount)),
                    customResponseData.disclaimer && (React.createElement(S.Disclaimer, { dangerouslySetInnerHTML: { __html: customResponseData.disclaimer } })))),
                React.createElement(S.ButtonsWrapper, null,
                    step > 0 && (React.createElement(Button, { buttonType: "default", onClick: (e) => {
                            e.preventDefault();
                            setStep(step - 1);
                        }, iconPosition: "left" }, props.translations?.['forms.affordabilityCalculator.previous'])),
                    step < StepFields.length - 1 && (React.createElement(Button, { buttonType: "tertiary", onClick: (e) => {
                            e.preventDefault();
                            validateStep(step);
                        } }, props.translations?.['forms.affordabilityCalculator.next'])),
                    step === StepFields.length - 1 && (React.createElement(Button, { buttonType: "tertiary", type: "submit" }, props.translations?.['forms.affordabilityCalculator.submit'])),
                    step === 5 && customResponseData && (React.createElement(LinkButton, { buttonType: "tertiary", href: "#sic-product-cards", icon: SystemIcon.ArrowDown }, customResponseData.buttonText))),
                status === 'pending' && (React.createElement(LoadingIndicator, { label: props.translations?.['forms.global.submitting'], layout: "absolute", type: ContentType.Default })))),
        status === 'fulfilled' &&
            customResponseData &&
            customResponseData.products &&
            customResponseData.products.length > 0 ? (React.createElement(S.ProductCards, { id: "sic-product-cards" }, customResponseData.products.map((product, i) => (React.createElement(ProductCard, { key: product.id, ...product, type: ContentType.Savings, translations: props.translations, zIndex: (customResponseData.products ?? []).length - i }))))) : null));
    async function validateStep(index) {
        const result = await trigger(StepFields[index], {
            shouldFocus: true,
        });
        if (!result) {
            return;
        }
        refContainer.current?.scrollIntoView({ behavior: 'smooth' });
        return setStep(index + 1);
    }
    function renderStepOneFields() {
        return (React.createElement(React.Fragment, null,
            React.createElement(FormField, { label: props.translations?.['forms.affordabilityCalculator.step1.firstName.label'], tooltip: props.translations?.['forms.affordabilityCalculator.step1.firstName.tooltip'], name: "firstName", error: errors.firstName, translations: props.translations },
                React.createElement(Textbox, { placeholder: props.translations?.['forms.affordabilityCalculator.step1.firstName.placeholder'], ...register('firstName', {
                        required: props.translations?.['forms.affordabilityCalculator.step1.firstName.error'] ??
                            false,
                    }) })),
            props.borrowerTypeOptions && props.borrowerTypeOptions.length > 0 && (React.createElement(FormField, { label: props.translations?.['forms.affordabilityCalculator.step1.borrowerType.label'], tooltip: props.translations?.['forms.affordabilityCalculator.step1.borrowerType.tooltip'], name: "borrowerType", error: errors.borrowerType, translations: props.translations },
                React.createElement(Select, { ...register('borrowerType', {
                        required: props.translations?.['forms.affordabilityCalculator.step1.borrowerType.error'] ??
                            false,
                    }) },
                    React.createElement("option", { value: "" }, props.translations?.['forms.affordabilityCalculator.step1.borrowerType.placeholder']),
                    props.borrowerTypeOptions.map((option) => (React.createElement("option", { key: `affordability-borrower-type-${option.value}`, value: option.value }, option.label)))))),
            React.createElement(FormField, { label: props.translations?.['forms.affordabilityCalculator.step1.noApplicants.label'], tooltip: props.translations?.['forms.affordabilityCalculator.step1.noApplicants.tooltip'], name: "noApplicants", error: errors.noApplicants, translations: props.translations },
                React.createElement(NumberField, { ...register('noApplicants'), onMinusClick: (value) => {
                        setValue('noApplicants', parseInt(value));
                    }, onPlusClick: (value) => {
                        setValue('noApplicants', parseInt(value));
                        if (value && parseInt(value) === 2) {
                            setValue('noAdults', 2);
                        }
                    }, onChange: (e) => {
                        const value = e.target.value;
                        if (value && parseInt(value) === 2) {
                            setValue('noAdults', 2);
                        }
                    }, minusLabel: props.translations?.['global.forms.number.minus'], plusLabel: props.translations?.['global.forms.number.plus'], value: formData.noApplicants, min: "1", max: "2" })),
            React.createElement(FormSection, { title: props.translations?.['forms.affordabilityCalculator.step1.livingProperty.label'] },
                React.createElement(FormField, { label: props.translations?.['forms.affordabilityCalculator.step1.noAdults.label'], tooltip: props.translations?.['forms.affordabilityCalculator.step1.noAdults.tooltip'], name: "noAdults", error: errors.noAdults, translations: props.translations },
                    React.createElement(NumberField, { ...register('noAdults'), onMinusClick: (value) => {
                            setValue('noAdults', parseInt(value));
                        }, onPlusClick: (value) => {
                            setValue('noAdults', parseInt(value));
                        }, minusLabel: props.translations?.['global.forms.number.minus'], plusLabel: props.translations?.['global.forms.number.plus'], value: formData.noAdults, min: "1", max: "2", disabled: formData.noApplicants === 2 })),
                React.createElement(FormField, { label: props.translations?.['forms.affordabilityCalculator.step1.noChildren.label'], tooltip: props.translations?.['forms.affordabilityCalculator.step1.noChildren.tooltip'], name: "noChildren", error: errors.noChildren, translations: props.translations },
                    React.createElement(NumberField, { ...register('noChildren'), onMinusClick: (value) => {
                            setValue('noChildren', parseInt(value));
                        }, onPlusClick: (value) => {
                            setValue('noChildren', parseInt(value));
                        }, minusLabel: props.translations?.['global.forms.number.minus'], plusLabel: props.translations?.['global.forms.number.plus'], value: formData.noChildren })),
                React.createElement(FormField, { label: props.translations?.['forms.affordabilityCalculator.step1.noYoungChildren.label'], tooltip: props.translations?.['forms.affordabilityCalculator.step1.noYoungChildren.tooltip'], name: "noYoungChildren", error: errors.noYoungChildren, translations: props.translations },
                    React.createElement(NumberField, { ...register('noYoungChildren'), onMinusClick: (value) => {
                            setValue('noYoungChildren', parseInt(value));
                        }, onPlusClick: (value) => {
                            setValue('noYoungChildren', parseInt(value));
                        }, minusLabel: props.translations?.['global.forms.number.minus'], plusLabel: props.translations?.['global.forms.number.plus'], value: formData.noYoungChildren })))));
    }
    function renderStepTwoFields() {
        return (React.createElement(React.Fragment, null, Array.from({ length: formData.noApplicants }).map((_, i) => (React.createElement(FormSection, { key: `affordability-applicant-${i}`, title: props.translations?.[`forms.affordabilityCalculator.step2.applicant${i + 1}`] }, StepFields[1].map((field) => (React.createElement(FormField, { key: `affordability-${field}-${i}`, label: props.translations?.[`forms.affordabilityCalculator.step2.${field}.label`], tooltip: props.translations?.[`forms.affordabilityCalculator.step2.${field}.tooltip`], name: `${field}.${i}`, error: errors[field]?.[i], translations: props.translations },
            React.createElement(Textbox, { prefix: "\u00A3", placeholder: props.translations?.[`forms.affordabilityCalculator.step2.${field}.placeholder`], ...register(`${field}.${i}`, {
                    valueAsNumber: true,
                    required: props.translations?.[`forms.affordabilityCalculator.step2.${field}.error`] ??
                        false,
                    validate: (value) => {
                        if (value && isNaN(value)) {
                            return props.translations?.[`forms.affordabilityCalculator.step2.${field}.errorType`];
                        }
                        return true;
                    },
                }), setValue: (value) => setValue(`${field}.${i}`, Number(value)) })))))))));
    }
    function renderStepThreeFields() {
        let total = 0;
        StepFields[2].forEach((field, i) => {
            if (i === 0) {
                formData[field]?.forEach((value) => {
                    const val = Number(value);
                    if (!isNaN(val)) {
                        total += val;
                    }
                });
            }
            else if (i > 0 && formData[field]) {
                const weighting = IncomeWeighting.find((w) => w.name === field);
                const val = Number(formData[field]);
                if (!isNaN(val)) {
                    total += weightedValue(val, weighting);
                }
            }
        });
        return (React.createElement(React.Fragment, null,
            React.createElement(FormSection, { title: props.translations?.[`forms.affordabilityCalculator.step3.netMonthlyIncome.fieldset${formData.noApplicants === 2 ? '.both' : ''}`] }, Array.from({ length: formData.noApplicants }).map((_, i) => (React.createElement(FormField, { key: `affordability-net-monthly-income-${i}`, label: props.translations?.['forms.affordabilityCalculator.step3.netMonthlyIncome.label']?.replaceAll('~~applicant~~', formData.noApplicants === 2
                    ? props.translations?.[`forms.affordabilityCalculator.step3.applicant${i + 1}`]
                    : ''), tooltip: props.translations?.['forms.affordabilityCalculator.step3.netMonthlyIncome.tooltip'], name: "netMonthlyIncome", error: errors.netMonthlyIncome?.[i], translations: props.translations },
                React.createElement(Textbox, { prefix: "\u00A3", placeholder: props.translations?.[`forms.affordabilityCalculator.step3.netMonthlyIncome.placeholder`], ...register(`netMonthlyIncome.${i}`, {
                        valueAsNumber: true,
                        required: props.translations?.['forms.affordabilityCalculator.step3.netMonthlyIncome.error'] ?? false,
                        validate: (value) => {
                            if (value && isNaN(value)) {
                                return props.translations?.['forms.affordabilityCalculator.step3.netMonthlyIncome.errorType'];
                            }
                            return true;
                        },
                    }), setValue: (value) => setValue(`netMonthlyIncome.${i}`, Number(value)) }))))),
            React.createElement(FormSection, null,
                React.createElement(S.FormSectionAccordionTitle, null,
                    React.createElement("button", { "aria-expanded": incomeActive, onClick: (e) => {
                            e.preventDefault();
                            setIncomeActive(!incomeActive);
                        } },
                        props.translations?.['forms.affordabilityCalculator.step3.additional.fieldset'],
                        ' ',
                        getSystemIcon(SystemIcon.ChevronDown))),
                React.createElement(S.AccordionFormFields, { variants: {
                        closed: {
                            height: 0,
                            transitionEnd: {
                                visibility: 'hidden',
                            },
                        },
                        opened: {
                            visibility: 'visible',
                            height: 'auto',
                        },
                    }, initial: incomeActive ? 'opened' : 'closed', animate: incomeActive ? 'opened' : 'closed' }, StepFields[2].map((field, i) => {
                    if (i === 0) {
                        return null;
                    }
                    return (React.createElement(FormField, { key: `affordability-${field}`, label: props.translations?.[`forms.affordabilityCalculator.step3.${field}.label`], tooltip: props.translations?.[`forms.affordabilityCalculator.step3.${field}.tooltip`], name: field, error: errors[field], translations: props.translations },
                        React.createElement(Textbox, { prefix: "\u00A3", placeholder: props.translations?.[`forms.affordabilityCalculator.step3.${field}.placeholder`], ...register(field, {
                                valueAsNumber: true,
                                validate: (value) => {
                                    if (value && isNaN(value)) {
                                        return props.translations?.[`forms.affordabilityCalculator.step3.${field}.errorType`];
                                    }
                                    return true;
                                },
                            }), setValue: (value) => setValue(field, Number(value)) })));
                }))),
            React.createElement(CalculatorResponse, { properties: [
                    {
                        label: props.translations?.['forms.affordabilityCalculator.step3.total.label'] ?? '',
                        value: total.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' }),
                    },
                ], type: props.type, translations: props.translations })));
    }
    function renderStepFourFields() {
        let total = 0;
        StepFields[3].forEach((field, i) => {
            if (i > 0 && formData[field]) {
                const weighting = ExpenditureWeighting.find((w) => w.name === field);
                const val = Number(formData[field]);
                if (!isNaN(val)) {
                    total += weightedValue(val, weighting);
                }
            }
        });
        return (React.createElement(React.Fragment, null,
            React.createElement(FormSection, { title: props.translations?.[`forms.affordabilityCalculator.step4.fieldset${formData.noApplicants === 2 ? '.both' : ''}`] }, StepFields[3].map((field, i) => (React.createElement(FormField, { key: `affordability-${field}`, label: props.translations?.[`forms.affordabilityCalculator.step4.${field}.label`], tooltip: props.translations?.[`forms.affordabilityCalculator.step4.${field}.tooltip`], name: field, error: errors[field], translations: props.translations },
                React.createElement(Textbox, { prefix: "\u00A3", placeholder: props.translations?.[`forms.affordabilityCalculator.step4.${field}.placeholder`], ...register(field, {
                        valueAsNumber: true,
                        validate: (value) => {
                            if (value && isNaN(value)) {
                                return props.translations?.[`forms.affordabilityCalculator.step4.${field}.errorType`];
                            }
                            return true;
                        },
                    }), readOnly: i === 1, setValue: (value) => setValue(field, Number(value)), ...(field === 'creditCardPayments' ? { initialValue: creditCardPayments } : {}) }))))),
            React.createElement(CalculatorResponse, { properties: [
                    {
                        label: props.translations?.['forms.affordabilityCalculator.step4.total.label'] ?? '',
                        value: total.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' }),
                    },
                ], type: props.type, translations: props.translations })));
    }
    function renderStepFiveFields() {
        const percentage = formData.purchasePrice && formData.borrowingAmount
            ? formData.borrowingAmount / formData.purchasePrice
            : 0;
        return (React.createElement(React.Fragment, null,
            StepFields[4].map((field, i) => (React.createElement(FormField, { key: `affordability-${field}`, label: props.translations?.[`forms.affordabilityCalculator.step5.${field}.label`], tooltip: props.translations?.[`forms.affordabilityCalculator.step5.${field}.tooltip`], name: field, error: errors[field], translations: props.translations }, i === 0 ? (React.createElement(Select, { ...register(field, {
                    required: props.translations?.[`forms.affordabilityCalculator.step5.${field}.error`] ??
                        false,
                }) },
                React.createElement("option", { value: "" }, props.translations?.[`forms.affordabilityCalculator.step5.${field}.placeholder`]),
                props.locationOptions.map((option) => (React.createElement("option", { key: `affordability-location-option-${option.value}`, value: option.value }, option.label))))) : i === 1 ? (React.createElement(NumberField, { ...register(field), onMinusClick: (value) => {
                    setValue(field, parseInt(value));
                }, onPlusClick: (value) => {
                    setValue(field, parseInt(value));
                }, minusLabel: props.translations?.['global.forms.number.minus'], plusLabel: props.translations?.['global.forms.number.plus'], value: formData[field], min: "5", max: "35" })) : (React.createElement(Textbox, { prefix: "\u00A3", placeholder: props.translations?.[`forms.affordabilityCalculator.step5.${field}.placeholder`], ...register(field, {
                    valueAsNumber: true,
                    required: props.translations?.[`forms.affordabilityCalculator.step5.${field}.error`] ??
                        false,
                    validate: (value) => {
                        if (value && isNaN(value)) {
                            return props.translations?.[`forms.affordabilityCalculator.step5.${field}.errorType`];
                        }
                        // Check that the value of the property exceeds 75,000 and is less than 5,000,000
                        if (i === 2 && (value < 75000 || value > 5000000)) {
                            return props.translations?.[`forms.affordabilityCalculator.step5.${field}.errorAmount`];
                        }
                        // Check that the borrowing amount exceeds 50,000 and is less than 500,000
                        if (i === 3 && (value < 50000 || value > 500000)) {
                            return props.translations?.[`forms.affordabilityCalculator.step5.${field}.errorAmount`];
                        }
                        return true;
                    },
                }), ...(i === 3
                    ? {
                        onBlur: () => {
                            if (formData.purchasePrice && formData.borrowingAmount) {
                                if (formData.borrowingAmount > formData.purchasePrice) {
                                    setValue('borrowingAmount', formData.purchasePrice);
                                }
                            }
                        },
                    }
                    : {}), setValue: (value) => setValue(field, Number(value)) }))))),
            React.createElement(CalculatorResponse, { properties: [
                    {
                        label: props.translations?.['forms.affordabilityCalculator.step5.total.label'] ?? '',
                        value: percentage.toLocaleString('en-GB', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                            style: 'percent',
                        }),
                        tooltip: props.translations?.['forms.affordabilityCalculator.step5.total.tooltip'] ?? '',
                    },
                ], type: props.type, translations: props.translations })));
    }
    function weightedValue(value, weighting) {
        if (!weighting) {
            return value;
        }
        return weighting.weight === 0 ? 0 : value * weighting.weight;
    }
};
export default withMotionMax(AffordabilityCalculator);
